import { ArrowRight, Plus } from "@untitledui/icons";
import type { CSSProperties, ReactNode } from "react";
import { Button } from "@/components/base/buttons/button";
import { cx } from "@/utils/cx";
import { EmptyStatePortrait } from "./empty-state-portrait";
import { Glyph } from "./glyph";
import { usePrefersReducedMotion } from "./use-prefers-reduced-motion";

const SPARKLES_ICON =
  "m6.5 13 .784 1.569c.266.53.399.796.576 1.026a3 3 0 0 0 .545.545c.23.177.495.31 1.026.575L11 17.5l-1.569.785c-.53.265-.796.398-1.026.575a3 3 0 0 0-.545.545c-.177.23-.31.495-.576 1.026L6.5 22l-.784-1.569c-.266-.53-.399-.796-.576-1.026a3 3 0 0 0-.545-.545c-.23-.177-.495-.31-1.026-.575L2 17.5l1.569-.785c.53-.265.796-.398 1.026-.575a3 3 0 0 0 .545-.545c.177-.23.31-.495.576-1.026L6.5 13ZM15 2l1.179 3.064c.282.734.423 1.1.642 1.409a3 3 0 0 0 .706.706c.309.22.675.36 1.409.642L22 9l-3.064 1.179c-.734.282-1.1.423-1.409.642a3 3 0 0 0-.706.706c-.22.309-.36.675-.642 1.409L15 16l-1.179-3.064c-.282-.734-.423-1.1-.642-1.409a3 3 0 0 0-.706-.706c-.309-.22-.675-.36-1.409-.642L8 9l3.064-1.179c.734-.282 1.1-.423 1.409-.642a3 3 0 0 0 .706-.706c.22-.309.36-.675.642-1.409L15 2Z";

/**
 * Prototype-exact gradient stops, scoped to the empty state so the shared
 * `unbox-dark-brand-*` tokens other surfaces rely on are left untouched.
 */
const GRADIENT_TOKENS = {
  "--color-unbox-dark-brand-start": "#322047",
  "--color-unbox-dark-brand-mid": "#4b2e61",
  "--color-unbox-dark-brand-end": "#3d2653",
} as CSSProperties;

const TEXT_ENTRANCE = "fade-in-up 0.5s ease-out 0.05s both";

type EmptyStateAction = {
  label: string;
  onClick: () => void;
  /** "arrow" trails a right arrow (default); "plus"/"sparkles" lead with that icon. */
  icon?: "arrow" | "plus" | "sparkles";
};

export type EmptyStatePageProps = {
  /** Small uppercased label pinned to the top-left. */
  eyebrow: string;
  headline: ReactNode;
  /** One paragraph, or several rendered as stacked paragraphs. */
  subline: string | string[];
  /** Measure cap, in ch. Applied to the text block, or to the sublines when the headline can't wrap. */
  measureCh?: number;
  /** For a long headline that reads on one line: don't cap the block, cap the sublines instead. */
  headlineNoWrap?: boolean;
  portraitSrc: string;
  portraitMaskLeft?: string;
  portraitMaskBottom?: string | null;
  /** Replaces the portrait's position/height/opacity classes when a page needs it. */
  portraitClassName?: string;
  /** Vertical gap between the text, device and actions. */
  contentGapClassName?: string;
  /** The page-specific glass-card "device", already wired for its own motion. */
  device?: ReactNode;
  /** Some pages (e.g. read-only activity) have no action at all. */
  primaryAction?: EmptyStateAction;
  secondaryLink?: EmptyStateAction;
  /** When the actions fade in, in seconds — set to just after the device settles. */
  actionsDelaySeconds?: number;
};

export function EmptyStatePage({
  eyebrow,
  headline,
  subline,
  measureCh = 44,
  headlineNoWrap = false,
  portraitSrc,
  portraitMaskLeft,
  portraitMaskBottom,
  portraitClassName,
  contentGapClassName = "gap-9",
  device,
  primaryAction,
  secondaryLink,
  actionsDelaySeconds = 1.2,
}: EmptyStatePageProps) {
  const reduceMotion = usePrefersReducedMotion();
  const measure = `${measureCh}ch`;
  const sublineStyle = headlineNoWrap ? { maxWidth: measure } : undefined;

  return (
    <div
      className="relative flex min-h-full w-full flex-1 justify-center bg-unbox-dark-brand-start"
      style={GRADIENT_TOKENS}
    >
      <div className="pointer-events-none absolute top-10 left-16 z-10">
        <p className="text-xs font-semibold tracking-wider text-white/50 uppercase">{eyebrow}</p>
      </div>

      <div className="flex w-full flex-1 items-stretch">
        <div
          className={cx(
            "relative isolate flex min-h-full w-full flex-1 flex-col items-start justify-center overflow-hidden bg-linear-to-r from-unbox-dark-brand-start via-unbox-dark-brand-mid via-57% to-unbox-dark-brand-end px-16 py-20",
            contentGapClassName,
          )}
        >
          <EmptyStatePortrait
            src={portraitSrc}
            maskLeft={portraitMaskLeft}
            maskBottom={portraitMaskBottom}
            className={portraitClassName}
          />

          <div
            className={cx("flex flex-col items-start gap-3 text-left", headlineNoWrap && "max-w-full")}
            style={{
              maxWidth: headlineNoWrap ? undefined : measure,
              animation: reduceMotion ? undefined : TEXT_ENTRANCE,
            }}
          >
            <h1
              className={cx(
                "text-[32px] leading-[40px] font-semibold text-white",
                headlineNoWrap && "md:whitespace-nowrap",
              )}
            >
              {headline}
            </h1>
            {(Array.isArray(subline) ? subline : [subline]).map((line, index) => (
              <p key={index} className="text-base leading-6 text-brand-100" style={sublineStyle}>
                {line}
              </p>
            ))}
          </div>

          {device}

          {(primaryAction || secondaryLink) && (
            <div
              className="flex flex-col items-start gap-4"
              style={{
                animation: reduceMotion ? undefined : `fade-in-up 0.5s ease-out ${actionsDelaySeconds}s both`,
              }}
            >
              {primaryAction &&
                (primaryAction.icon === "plus" ? (
                  <Button color="secondary" size="lg" iconLeading={Plus} onClick={primaryAction.onClick}>
                    {primaryAction.label}
                  </Button>
                ) : primaryAction.icon === "sparkles" ? (
                  <Button
                    color="secondary"
                    size="lg"
                    iconLeading={<Glyph d={SPARKLES_ICON} size={18} />}
                    onClick={primaryAction.onClick}
                  >
                    {primaryAction.label}
                  </Button>
                ) : (
                  <Button color="secondary" size="lg" iconTrailing={ArrowRight} onClick={primaryAction.onClick}>
                    {primaryAction.label}
                  </Button>
                ))}
              {secondaryLink && (
                <button
                  type="button"
                  onClick={secondaryLink.onClick}
                  className="text-sm font-medium text-brand-100 underline decoration-white/30 underline-offset-4 transition-colors duration-100 hover:text-white"
                >
                  {secondaryLink.label}
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
